import type { Unit } from '@ops/contracts'

import { cx } from '../lib/cx'
import { formatMetricValue } from '../lib/format'
import { SEVERIDAD_RELLENO, type Severidad } from './tokens'

/**
 * Barra de distribución: segmentos apilados que suman el total, con leyenda.
 * Es el bloque de salud del portfolio y cualquier otro "cómo se reparte esto".
 *
 * Dos decisiones de accesibilidad que ya pasaron auditoría:
 *  - el número dentro de un segmento va en `--text`, **nunca en blanco**: blanco
 *    sobre el verde de marca da 2,21:1;
 *  - cuando dos segmentos comparten color, el segundo lleva `trama` (rayado
 *    diagonal) y la leyenda repite la misma trama, así el mapeo es 1 a 1 sin
 *    depender del color.
 *
 * La leyenda dice cantidad y porcentaje en palabras: la barra sola no se lee.
 *
 * **Un solo eje.** `cantidad`, `total` y `pct` tienen que ser la misma magnitud:
 * la leyenda los imprime pegados ("Enterprise 12 (76,1%)") y el ancho del
 * segmento es `pct`, así que mezclar dos ejes produce una frase que se lee como
 * una división que no da. Pasó: esta barra recibía la cantidad de clientes con
 * el porcentaje del MRR. `unidad` existe justamente para que el eje sea
 * explícito en el call site y no un `number` sin nombre.
 */
export interface ShareSegment {
  id: string
  /** Nombre del segmento. Va en la leyenda, en texto. */
  label: string
  /**
   * Lo que aporta el segmento, en la unidad de la barra. Tiene que ser el mismo
   * eje que `pct`: `cantidad / total × 100` debe dar `pct`.
   *
   * `null` = sin dato. No se imprime cero: un cero afirma que el segmento aporta
   * nada, y lo que pasa es que no se sabe.
   */
  cantidad: number | null
  /** 0–100. Define el ancho del segmento. */
  pct: number
  severidad: Severidad
  /** Rayado diagonal, para el segundo segmento que comparte color. */
  trama?: boolean
}

export interface ShareBarProps {
  segmentos: readonly ShareSegment[]
  /** Qué se reparte, ej. "Portfolio por estado". Nombre accesible del grupo. */
  label: string
  /** Total en la misma unidad que `cantidad`, para el texto de la leyenda. */
  total: number
  /** Unidad de `cantidad` y `total`. El eje de la barra, dicho en el call site. */
  unidad?: Unit
  /** false oculta la leyenda, cuando ya está en otra parte de la pantalla. */
  leyenda?: boolean
  /** Muestra el valor dentro del segmento si entra. */
  numeroDentro?: boolean
  className?: string
}

const TRAMA =
  'bg-[repeating-linear-gradient(45deg,rgba(0,0,0,.34)_0_2px,rgba(0,0,0,0)_2px_6px)]'

/**
 * Ancho mínimo para meter el valor adentro del segmento. Un importe en pesos
 * ("$212.400") ocupa el triple que un conteo ("19"), así que el umbral depende
 * de la unidad: con el de `count` un importe quedaría cortado por la mitad.
 */
const PCT_MINIMO_DENTRO: Partial<Record<Unit, number>> = { usd: 18 }

/** Cantidad de un segmento, en la unidad de la barra. `null` no imprime cero. */
function textoCantidad(cantidad: number | null, unidad: Unit): string {
  return cantidad === null ? 'sin dato' : formatMetricValue(cantidad, unidad)
}

export function ShareBar({
  segmentos,
  label,
  total,
  unidad = 'count',
  leyenda = true,
  numeroDentro = true,
  className,
}: ShareBarProps) {
  const minimoDentro = PCT_MINIMO_DENTRO[unidad] ?? 8

  return (
    <div
      data-testid="share-bar"
      className={cx('flex min-w-0 flex-col gap-2', className)}
    >
      <div
        role="img"
        aria-label={`${label}: ${segmentos
          .map(
            (s) =>
              `${s.label} ${textoCantidad(s.cantidad, unidad)} de ${formatMetricValue(total, unidad)} (${formatMetricValue(s.pct, 'percent')})`,
          )
          .join(', ')}`}
        className="flex h-5 gap-0.5 overflow-hidden rounded bg-n100"
      >
        {segmentos.map((segmento) => (
          <span
            key={segmento.id}
            data-testid="share-bar-seg"
            data-segmento={segmento.id}
            style={{ width: `${Math.max(0, Math.min(100, segmento.pct))}%` }}
            className={cx(
              'flex min-w-0.5 items-center justify-center overflow-hidden text-micro font-bold tabular text-text shadow-[inset_0_0_0_1px_rgba(0,0,0,.28)]',
              SEVERIDAD_RELLENO[segmento.severidad],
              segmento.trama === true && TRAMA,
            )}
          >
            {numeroDentro &&
            segmento.cantidad !== null &&
            segmento.pct >= minimoDentro
              ? formatMetricValue(segmento.cantidad, unidad)
              : null}
          </span>
        ))}
      </div>

      {leyenda ? (
        <ul className="flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-micro text-muted-strong">
          {segmentos.map((segmento) => (
            <li key={segmento.id} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cx(
                  'h-3 w-3 shrink-0 rounded-[2px] shadow-[inset_0_0_0_1px_rgba(0,0,0,.32)]',
                  SEVERIDAD_RELLENO[segmento.severidad],
                  segmento.trama === true && TRAMA,
                )}
              />
              <span>{segmento.label}</span>
              <b className="tabular font-semibold text-text">
                {textoCantidad(segmento.cantidad, unidad)}
              </b>
              <span className="tabular">
                ({formatMetricValue(segmento.pct, 'percent')})
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
